import React, { useState, useRef, useEffect } from 'react';
import { Camera, Upload, X, Loader, Check, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const QuickPhotoManager = ({ onClose, onPhotosUpdated }) => {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const MAX_PHOTOS = 6;
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

  useEffect(() => {
    loadExistingPhotos();
  }, []);

  const loadExistingPhotos = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/photos/my-photos`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const loadedPhotos = data.photos.map(photo => ({
          id: photo.id,
          url: `${process.env.REACT_APP_BACKEND_URL}${photo.url}`,
          verified: photo.verified,
          isPrimary: photo.is_primary
        }));
        setPhotos(loadedPhotos);
      }
    } catch (error) {
      console.error('Error loading photos:', error);
      setError('Failed to load photos');
    } finally {
      setLoading(false);
    }
  };

  const compressImage = (file, maxWidth = 1200, quality = 0.8) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        let { width, height } = img;
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(resolve, 'image/jpeg', quality);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    
    if (photos.length + files.length > MAX_PHOTOS) {
      setError(`You can only upload up to ${MAX_PHOTOS} photos total`);
      return;
    }

    setUploading(true);
    setError(null);

    for (const file of files) {
      // Validate file
      if (!ALLOWED_TYPES.includes(file.type)) {
        setError(`${file.name}: Please upload a JPEG, PNG, or WebP image`);
        continue;
      }
      
      if (file.size > MAX_FILE_SIZE) {
        setError(`${file.name}: Image must be less than 5MB`);
        continue;
      }

      try {
        // Compress image
        const compressedBlob = await compressImage(file);
        
        // Upload to backend
        const token = localStorage.getItem('token');
        const formData = new FormData();
        formData.append('file', compressedBlob, file.name);

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload-photo`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (response.ok) {
          const data = await response.json();
          const newPhoto = {
            id: data.photo_id,
            url: `${process.env.REACT_APP_BACKEND_URL}${data.url}`,
            verified: false,
            isPrimary: false
          };
          setPhotos(prev => [...prev, newPhoto]);
        } else {
          const error = await response.json();
          setError(error.detail || 'Failed to upload photo');
        }
      } catch (error) {
        console.error('Error uploading photo:', error);
        setError(`Failed to upload ${file.name}`);
      }
    }

    setUploading(false);
    event.target.value = ''; // Reset input
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/photos/${photoId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setPhotos(prev => prev.filter(p => p.id !== photoId));
      } else {
        setError('Failed to delete photo');
      }
    } catch (error) {
      console.error('Error deleting photo:', error);
      setError('Failed to delete photo');
    }
  };

  const handleSetPrimary = async (photoId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/photos/${photoId}/primary`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setPhotos(prev => prev.map(p => ({
          ...p,
          isPrimary: p.id === photoId
        })));
      }
    } catch (error) {
      console.error('Error setting primary photo:', error);
    }
  };

  const handleDone = () => {
    if (onPhotosUpdated) {
      onPhotosUpdated(photos);
    }
    if (onClose) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="bg-gradient-to-br from-purple-900/95 to-pink-900/95 border-white/20 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <CardTitle className="text-white text-2xl flex items-center justify-between">
            <span className="flex items-center">
              <Camera className="h-6 w-6 mr-3" />
              Manage Your Photos
            </span>
            <Button
              onClick={handleDone}
              variant="ghost"
              className="text-white hover:bg-white/10"
            >
              <X className="h-5 w-5" />
            </Button>
          </CardTitle>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {error && (
            <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 flex items-start">
              <AlertCircle className="h-5 w-5 text-red-400 mr-3 mt-0.5" />
              <p className="text-red-200">{error}</p>
            </div>
          )}

          {/* Photo Grid */}
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader className="h-8 w-8 text-white animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {photos.map((photo) => (
                <div key={photo.id} className="relative group">
                  <img
                    src={photo.url}
                    alt="Profile"
                    className="w-full aspect-square object-cover rounded-lg"
                  />
                  {photo.isPrimary && (
                    <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-xs font-semibold">
                      Primary
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                    {!photo.isPrimary && (
                      <Button
                        onClick={() => handleSetPrimary(photo.id)}
                        size="sm"
                        className="bg-green-500 hover:bg-green-600"
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Set Primary
                      </Button>
                    )}
                    <Button
                      onClick={() => handleDeletePhoto(photo.id)}
                      size="sm"
                      variant="destructive"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}

              {/* Upload Button */}
              {photos.length < MAX_PHOTOS && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="aspect-square border-2 border-dashed border-white/30 rounded-lg flex flex-col items-center justify-center hover:border-white/50 hover:bg-white/5 transition-all disabled:opacity-50"
                >
                  {uploading ? (
                    <Loader className="h-8 w-8 text-white animate-spin" />
                  ) : (
                    <>
                      <Upload className="h-8 w-8 text-white mb-2" />
                      <span className="text-white text-sm">Upload Photo</span>
                      <span className="text-white/60 text-xs mt-1">
                        {photos.length}/{MAX_PHOTOS}
                      </span>
                    </>
                  )}
                </button>
              )}
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />

          {/* Tips */}
          <div className="bg-white/5 border border-white/10 rounded-lg p-4">
            <h4 className="text-white font-semibold mb-2">Photo Tips:</h4>
            <ul className="text-white/70 text-sm space-y-1">
              <li>• Upload up to {MAX_PHOTOS} photos</li>
              <li>• First photo will be your profile picture</li>
              <li>• Use clear, well-lit photos</li>
              <li>• Show your face clearly in at least one photo</li>
              <li>• Max file size: 5MB per photo</li>
            </ul>
          </div>

          {/* Done Button */}
          <Button
            onClick={handleDone}
            className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
            size="lg"
          >
            <Check className="h-5 w-5 mr-2" />
            Done ({photos.length} {photos.length === 1 ? 'photo' : 'photos'})
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default QuickPhotoManager;
